import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  input,
  OnDestroy,
  signal,
  ViewChild,
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { User, UserStore } from '../../../state/user.repository';
import {
  combineLatest,
  combineLatestWith,
  distinctUntilChanged,
  filter,
  map,
  merge,
  ReplaySubject,
  scan,
  Subject,
  switchMap,
  withLatestFrom,
} from 'rxjs';
import { VideoTile } from '../video-tile/video-tile';
import { VideoFullscreen } from '../video-fullscreen/video-fullscreen';
import { UserSettingsStore } from '../../../state/user-settings.repository';
import { UserService } from '../../../core/services/user.service';
import {
  presentationEpoch,
  resolveStagedId,
  sortVideoList,
  stabilizeVisibleList,
} from '../../../shared/helpers/video-list';
import { VideoStageService } from '../video-stage.service';
import {
  computeVideoGridLayout,
  GRID_GAP_PX,
  GridSize,
  gridSizeOf,
} from '../../../shared/helpers/video-grid-layout';

type ContainerSize = { width: number; height: number };

// Tiles shown per container size while the "visible tiles" setting is 'auto'.
const AUTO_SLOTS: Record<GridSize, number> = { small: 2, medium: 6, large: 9 };
// A staged tile caps the others so the stage keeps most of the space.
const STAGE_SLOTS: Partial<Record<GridSize, number>> = { medium: 4, large: 5 };
// The pinned column only fits a short stack.
const PINNED_SLOTS = 3;

// Older WebKit only exposes the prefixed fullscreen API; on entry it fires
// `webkitfullscreenchange` while `document.fullscreenElement` stays null, so
// every check has to consult both forms or fullscreen tears down immediately.
type WebkitDocument = Document & {
  webkitFullscreenElement?: Element;
  webkitExitFullscreen?: () => void;
};
type WebkitElement = HTMLElement & { webkitRequestFullscreen?: () => void };

function getFullscreenElement(): Element | null {
  return document.fullscreenElement ?? (document as WebkitDocument).webkitFullscreenElement ?? null;
}

// iPhone Safari supports neither form on anything but <video> — there the
// fullscreen dialog stays a modal-only pseudo-fullscreen.
function supportsElementFullscreen(el: HTMLElement): boolean {
  return (
    typeof el.requestFullscreen === 'function' ||
    typeof (el as WebkitElement).webkitRequestFullscreen === 'function'
  );
}

@Component({
  selector: 'andor-video',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [VideoTile, VideoFullscreen],
  templateUrl: './video.html',
  styleUrl: './video.scss',
})
export class Video implements OnDestroy, AfterViewInit {
  private readonly userService = inject(UserService);
  private readonly userStore = inject(UserStore);
  private readonly userSettingsStore = inject(UserSettingsStore);
  private readonly stage = inject(VideoStageService);

  mode = input<'pinned' | 'main'>('main');
  @ViewChild('viewport') viewport!: ElementRef<HTMLDivElement>;
  @ViewChild('fullscreenDialog') fullscreenDialog!: ElementRef<HTMLDialogElement>;
  @ViewChild('fullscreenTarget') fullscreenTarget!: ElementRef<HTMLDivElement>;
  // The tile shown large: the latest screen share or the viewer's own pick.
  stagedUserId = signal<string | null>(null);
  // The breakout room's users from the last pipeline run, for the stage toggle.
  private _roomUsers: User[] = [];
  fullscreenUser = signal<User | null>(null);
  // Lets the app manually re-run the visible-user pipeline, for example after
  // fullscreen ends — the pipeline is paused while fullscreen is active.
  refresh$ = new Subject<void>();
  protected readonly gridGap = GRID_GAP_PX;

  // ReplaySubject(1) so the pipeline waits for the first real measurement
  // (emitted synchronously in ngAfterViewInit) rather than starting from a
  // wrong seed and re-firing once the ResizeObserver corrects it.
  private readonly _containerSize$ = new ReplaySubject<ContainerSize>(1);
  private readonly _containerSize = toSignal(this._containerSize$);
  private readonly _gridSize$ = this._containerSize$.pipe(
    map(({ width, height }) => gridSizeOf(width, height)),
    distinctUntilChanged()
  );
  private readonly _resizeObserver = new ResizeObserver(([entry]) => {
    if (entry) {
      const { width, height } = entry.contentRect;
      this._containerSize$.next({ width, height });
    }
  });
  private readonly _fullscreenChangeHandler = () => {
    if (!getFullscreenElement() && this.fullscreenUser() !== null) {
      this.stopFullscreen();
    }
  };

  visibleUsers = toSignal(this.stabilizedUsers$(), { initialValue: [] });

  layout = computed(() => {
    const size = this._containerSize();
    if (!size) return null;
    const count = this.visibleUsers().length;
    // The stage layout covers 2–6 tiles; beyond that the strip gets too small.
    const stage = this.mode() === 'main' && !!this.stagedUserId() && count >= 2 && count <= 6;
    return computeVideoGridLayout({ count, ...size, stage });
  });

  private stabilizedUsers$() {
    // refresh$ re-emits the latest user list on demand.
    const refreshedUsers$ = this.refresh$.pipe(
      withLatestFrom(this.userStore.videoListUsers$),
      map(([, users]) => users)
    );

    // Track only the own user's breakout room id so the filter re-evaluates
    // when it loads/changes, without re-firing on unrelated own-user updates.
    const ownBreakoutRoomId$ = this.userService.ownUser$.pipe(
      map((ownUser) => ownUser?.data.breakoutRoomId ?? ''),
      distinctUntilChanged()
    );

    return merge(this.userStore.videoListUsers$, refreshedUsers$).pipe(
      // Stop doing anything when there is a fullscreen active (save performance)
      filter(() => this.fullscreenUser() === null),
      // Re-filter when either the user list OR the own breakout room changes.
      // withLatestFrom would only re-fire on upstream emissions, so if the own
      // user loaded AFTER the last user-list emission the filter stayed stale.
      // The viewer's stage choice re-runs it the same way.
      combineLatestWith(ownBreakoutRoomId$, toObservable(this.stage.override)),
      map(([users, ownBreakoutRoomId, override]) => {
        const roomUsers = users.filter(
          (user) => (user.data.breakoutRoomId ?? '') === ownBreakoutRoomId
        );
        const { stagedId, superseded } = resolveStagedId(roomUsers, override);
        // A share started after the viewer's choice takes the stage back.
        if (superseded) this.stage.clear();
        this._roomUsers = roomUsers;
        this.stagedUserId.set(stagedId);
        // Sorting all users to put users with prio on top.
        return sortVideoList(roomUsers, stagedId);
      }),
      // Combine sorted candidates with the slot count for the current settings and size.
      switchMap((candidates) =>
        combineLatest([this.userSettingsStore.visibleTiles$, this._gridSize$]).pipe(
          map(([visibleTiles, size]) => ({
            candidates,
            maxSlots: this.maxSlots(visibleTiles, size),
          }))
        )
      ),
      // Stabilize the visible list by comparing previous and new users: only
      // replace users when a "better" candidate is available.
      scan(
        (visible: User[], { candidates, maxSlots }) =>
          stabilizeVisibleList(visible, candidates, maxSlots, this.stagedUserId()),
        []
      )
    );
  }

  /**
   * The tile's stage toggle: shows that user large, or closes the stage
   * when they already are. Only this viewer's grid changes; the choice
   * holds until someone starts a new screen share.
   */
  onStageToggled(user: User) {
    this.stage.setOverride({
      userId: this.stagedUserId() === user.id ? null : user.id,
      epoch: presentationEpoch(this._roomUsers),
    });
  }

  /**
   * Maximum number of visible tiles for the user's "visible tiles" setting
   * and the container size.
   */
  private maxSlots(visibleTiles: string, size: GridSize): number {
    if (this.mode() !== 'main') {
      return PINNED_SLOTS;
    }
    const stageSlots = this.stagedUserId() ? STAGE_SLOTS[size] : undefined;
    if (stageSlots) {
      return stageSlots;
    }
    return visibleTiles === 'auto' ? AUTO_SLOTS[size] : Number(visibleTiles);
  }

  ngAfterViewInit(): void {
    // Measure synchronously and seed the size stream before the
    // ResizeObserver fires asynchronously, so the first pipeline emission
    // already has the correct size.
    const { width, height } = this.viewport.nativeElement.getBoundingClientRect();
    this._containerSize$.next({ width, height });

    this._resizeObserver.observe(this.viewport.nativeElement);
    document.addEventListener('fullscreenchange', this._fullscreenChangeHandler);
    document.addEventListener('webkitfullscreenchange', this._fullscreenChangeHandler);

    this.refresh$.next();
  }

  ngOnDestroy(): void {
    this._resizeObserver.disconnect();
    document.removeEventListener('fullscreenchange', this._fullscreenChangeHandler);
    document.removeEventListener('webkitfullscreenchange', this._fullscreenChangeHandler);
  }

  // Runs synchronously inside the tile's click dispatch so the fullscreen
  // request still holds the transient user activation WebKit demands. The
  // dialog is always shown modally (top layer — the only way out of the
  // container-type/overflow traps between here and the viewport, and a closed
  // dialog is display:none); real element fullscreen is layered on top where
  // the platform has it — targeting the inner wrapper, since browsers reject
  // requestFullscreen() on <dialog> elements. On iPhone Safari element
  // fullscreen doesn't exist at all, and the modal alone is the fullscreen
  // experience.
  onFullscreenRequested(user: User) {
    this.fullscreenUser.set(user);

    const dialog = this.fullscreenDialog?.nativeElement;
    if (!dialog) return;
    if (!dialog.open) {
      try {
        dialog.showModal();
      } catch (e) {
        console.warn('failed to open fullscreen dialog', e);
      }
    }
    const target = this.fullscreenTarget?.nativeElement;
    if (target && supportsElementFullscreen(target) && !getFullscreenElement()) {
      try {
        // A rejection (revoked activation, platform quirk) just leaves the
        // modal-only pseudo-fullscreen, which is already presentable.
        target.requestFullscreen?.().catch(() => undefined) ??
          (target as WebkitElement).webkitRequestFullscreen?.();
      } catch {
        /* modal stays as the fallback */
      }
    }
  }

  stopFullscreen() {
    // Clear the signal before poking refresh$: the pipeline gates on
    // `fullscreenUser() === null`, so the reverse order drops the refresh and
    // the grid only recovers on the next store emission.
    this.fullscreenUser.set(null);
    this.refresh$.next();

    const dialog = this.fullscreenDialog?.nativeElement;
    if (dialog?.open) {
      dialog.close();
    }
    if (getFullscreenElement()) {
      try {
        document.exitFullscreen?.().catch(() => undefined) ??
          (document as WebkitDocument).webkitExitFullscreen?.();
      } catch {
        /* already out of fullscreen */
      }
    }
  }

  // The dialog can close without going through stopFullscreen (Esc cancels the
  // modal). The guard breaks the loop for closes that stopFullscreen itself
  // issued — the close event fires from a queued task, by which point the
  // signal is already null.
  onDialogClose() {
    if (this.fullscreenUser() !== null) {
      this.stopFullscreen();
    }
  }
}
